import { toCanvas } from 'html-to-image';
import { CARD_EXPORT_HEIGHT_PX, CARD_EXPORT_WIDTH_PX, CARD_HEIGHT_MM, CARD_WIDTH_MM } from '../components/card/cardTokens';

/** 2568 × 1620 px — ~760 DPI at physical card size. */
export const PNG_PIXEL_RATIO = 3;
/** 1712 × 1080 px embedded in the PDF — ~500 DPI, comfortably above print's 300. */
export const PDF_PIXEL_RATIO = 2;

/** Resolves once web fonts and every <img> inside `node` are decoded. */
export async function waitForCardAssets(node: HTMLElement): Promise<void> {
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    await document.fonts.ready;
  }
  const images = Array.from(node.querySelectorAll('img'));
  await Promise.all(
    images.map((img) =>
      img.complete && img.naturalWidth > 0
        ? Promise.resolve()
        : img.decode().catch(() => undefined)
    )
  );
}

/** Renders only the card node — never the surrounding page — to a canvas. */
export async function renderCardCanvas(node: HTMLElement, pixelRatio: number): Promise<HTMLCanvasElement> {
  await waitForCardAssets(node);
  return toCanvas(node, {
    width: CARD_EXPORT_WIDTH_PX,
    height: CARD_EXPORT_HEIGHT_PX,
    pixelRatio,
    backgroundColor: '#FFFFFF',
  });
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke after the browser has started the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG encoding failed'))), 'image/png');
  });
}

export async function exportCardPng(node: HTMLElement, fileName: string): Promise<void> {
  const canvas = await renderCardCanvas(node, PNG_PIXEL_RATIO);
  downloadBlob(await canvasToBlob(canvas), fileName);
}

/** Builds a single-page PDF sized exactly to the card (85.6 × 54 mm, zero margins). */
export async function buildCardPdf(node: HTMLElement, title: string): Promise<Blob> {
  const [canvas, { jsPDF }] = await Promise.all([
    renderCardCanvas(node, PDF_PIXEL_RATIO),
    import('jspdf'),
  ]);
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [CARD_WIDTH_MM, CARD_HEIGHT_MM],
    compress: true,
  });
  pdf.setProperties({ title, subject: 'Trplin Guide Identity', creator: 'Trplin' });
  // PNG keeps QR module edges lossless.
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, CARD_WIDTH_MM, CARD_HEIGHT_MM, undefined, 'FAST');
  return pdf.output('blob');
}

export async function exportCardPdf(node: HTMLElement, fileName: string, title: string): Promise<void> {
  downloadBlob(await buildCardPdf(node, title), fileName);
}

/** Prints via the dedicated print container (see PrintableGuideCard). */
export async function printCard(node: HTMLElement): Promise<void> {
  await waitForCardAssets(node);
  window.print();
}

const inlineCache = new Map<string, Promise<string | null>>();

async function fetchAsDataUrl(src: string): Promise<string> {
  if (src.startsWith('data:')) return src;
  const response = await fetch(src, { mode: 'cors', credentials: 'omit' });
  if (!response.ok) throw new Error(`Image request failed: ${response.status}`);
  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) throw new Error('Not an image');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Fetches an image as a data URL so exports never depend on a cross-origin
 * request mid-capture. Resolves null when the image is unavailable (or the
 * host blocks CORS) so the card falls back to the branded placeholder.
 */
export function loadInlineImage(src: string): Promise<string | null> {
  let pending = inlineCache.get(src);
  if (!pending) {
    pending = fetchAsDataUrl(src).catch(() => {
      inlineCache.delete(src);
      return null;
    });
    inlineCache.set(src, pending);
  }
  return pending;
}
